import React, { useState, useEffect } from "react";
import "./StoryReader.css"; // reuse same CSS for consistency
import { HiOutlineSpeakerWave } from "react-icons/hi2"; // or another similar one
import { PiCards } from "react-icons/pi";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { MdOutlineSearchOff } from "react-icons/md";
import { CiBookmarkPlus, CiBookmarkCheck } from "react-icons/ci";
import { IoFlagOutline } from "react-icons/io5";
import { useAuth } from "../../context/AuthContext";
import { submitReport } from "../../data/sidebarRepo";
import ReportPopup from "../flashcards//ReportPopup";  // adapt to your structure
import "./StorySidebar.css";


// at the top of StorySidebar.js (below imports)
const PRONOUN_ORDER = ["ich", "du", "er/sie/es", "wir", "ihr", "sie"];

// escape for regex
const escapeReg = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// remove a leading "pronoun[:]" from the form (case-insensitive)
const cleanForm = (pronoun, form) => {
  if (!form) return "";
  const re = new RegExp("^\\s*" + escapeReg(pronoun) + "\\s*:?", "i");
  return form.replace(re, "").trim();
};

export default function StorySidebar({
  selectedWord,
  wordData,
  loadingWord,
  story,
  onAddFlashcard,
  addedWords = [],
  savedWords, // ✅
}) {

  const { user } = useAuth();

  const [showReport, setShowReport] = useState(false);

  // ✅ early exit BEFORE accessing wordData
  if (!selectedWord || loadingWord || !wordData) {
    return (
      <div className="sidebar">
        <p>{loadingWord ? "Loading..." : "🪄 Click a word to see its translation."}</p>
      </div>
    );
  }


  const handleSidebarReportSubmit = async (data) => {
    try {
      if (!user?.uid) throw new Error("Not authenticated");
  
      await submitReport({
        uid: user.uid,
        source: "story-sidebar",
        reason: data.reason,
        details: data.details,
        payload: {
          word: wordData.word,
          lemma: wordData.lemma || null,
          pos: wordData.pos || null,
          storyId: story?.id || null,
          selectedWord,
        },
      });
  
      console.log("✅ Story word report saved!");
    } catch (err) {
      console.error("Error saving report:", err);
    }
  };
  

  const wordKey = (wordData?.word || "").toLowerCase();
  const lemmaKey = (wordData?.lemma || "").toLowerCase();  
  
  const isAdded =
    (wordKey && (addedWords.includes(wordKey) || savedWords?.includes(wordKey))) ||
    (lemmaKey && savedWords?.includes(lemmaKey));  
  

  return (
    <>
      {/* 🔹 Single match */}
      {wordData && !wordData.notFound && !wordData.multiple && (
        <div className="sidebar-entry">
          {/* Word + POS */}
          {/* Word + Article */}
          <div className="sidebar-header">
            <h2 className="sidebar-word">
              {wordData.article ? `${wordData.article} ` : ""}
              {wordData.word}
            </h2>

            {wordData.pos && <p className="sidebar-pos">{wordData.pos}</p>}

            {wordData.translations_pt && (
              <p className="sidebar-translation">
                {Array.isArray(wordData.translations_pt)
                  ? wordData.translations_pt.join(", ")
                  : wordData.translations_pt}
              </p>
            )}
          <div className="tooltip-container"></div>
            <div className="sidebar-actions">
              <button
                className="sidebar-flag-btn"
                title="Reportar problema"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReport(true);
                }}
              >
                <IoFlagOutline size={22} />
              </button>
              
              {wordData.audio && (
                <div
                  className="sidebar-audio-icon"
                  onClick={() => {
                    const audio = new Audio(wordData.audio);
                    audio.play();
                  }}
                >
                  <HiOutlineSpeakerWave size={22} />
                </div>
              )}
            

              <button
                className={`keyword-save-btn ${isAdded ? "saved" : ""}`}
                onClick={() => {
                  const matchingSentence = story.sentences.find((s) => {
                    const normalized = s.text.toLowerCase().replace(/[.,!?;]/g, "");
                    return normalized.split(" ").includes(selectedWord.toLowerCase());
                  });
                  onAddFlashcard(wordData, matchingSentence || null);
                }}
              >
                {isAdded ? (
                  <>
                    <CiBookmarkCheck size={20} /> Remover
                  </>
                ) : (
                  <>
                    <CiBookmarkPlus size={20} /> Salvar
                  </>
                )}
              </button>


            </div>

            {(wordData.plural || wordData.conjugations) && (
              <hr className="sidebar-divider" />
            )}


            {/* ✅ Plural block styled like conjugations */}
            {wordData.plural && (
              <div className="tense-block">
                <h4 className="tense-title">Plural</h4>
                <div className="tense-table single-row">
                  <div className="tense-row">
                    <span className="pronoun"></span>
                    <span className="verb-form">{wordData.plural}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
  
          {/* Conjugations */}
          {wordData.conjugations && (
            <div className="conjugations">
              {["Präsens", "Präteritum", "Perfekt", "Futur I"]
                .filter((tense) => wordData.conjugations[tense])
                .map((tense) => (
                  <div key={tense} className="tense-block">
                    <h4 className="tense-title">{tense}</h4>
                    <div className="tense-table">
                      {PRONOUN_ORDER.filter(
                        (p) => wordData.conjugations[tense][p]
                      ).map((pronoun) => (
                        <div className="tense-row" key={pronoun}>
                          <span className="pronoun">{pronoun}</span>
                          <span className="verb-form">
                            {cleanForm(
                              pronoun,
                              wordData.conjugations[tense][pronoun]
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
  
      {/* 🔹 Multiple results */}
      {wordData?.multiple && (
        <div>
          <h3>Multiple matches for “{selectedWord}”</h3>
          {wordData.multiple.map((entry, idx) => (
            <div key={idx} className="word-entry">
              <h4>
                {entry.article ? `${entry.article} ` : ""}
                {entry.word} ({entry.pos})
              </h4>
              {entry.translations_pt && (
                <p>
                  {Array.isArray(entry.translations_pt)
                    ? entry.translations_pt.join(", ")
                    : entry.translations_pt}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
  
      {/* 🔹 No translation */}
      {wordData?.notFound && (
        <div className="sidebar-not-found">
          <div className="sidebar-not-found-header">
            <MdOutlineSearchOff size={30} color="#f87171" />
            <span className="sidebar-not-found-title">Nenhuma tradução encontrada...</span>
          </div>
          <p className="sidebar-not-found-hint">Tente usar o botão <strong>CC</strong> para ver a tradução da frase completa.</p>
        </div>
      )}

      {showReport && (
        <ReportPopup
          onClose={() => setShowReport(false)}
          onSubmit={(data) =>
            handleSidebarReportSubmit(data, {
              word: wordData.word,
              lemma: wordData.lemma,
              pos: wordData.pos,
              plural: wordData.plural,
              translations: wordData.translations_pt,
              storyId: story?.id,
              selectedWord,
            })
          }
        />
      )}

    </>
  );
}  

